import React from 'react'
import { createPortal } from 'react-dom'
import styled from '@emotion/styled'
import { colors, font, spacing } from '@mobi/component-library/Foundations'
import { Tray } from '@mobi/component-library/Overlays/Tray'
import { Icon } from '@mobi/component-library/Assets/Icon'
import type { SortFilter, SortOrder } from '@core/Areas/BetLoop/types'
import { trackBetLoopSortFeed } from '@core/Areas/BetLoop/helpers/analytics'
import type { SortOption } from './types'
import { SortButtonBar } from './Components/SortButtonBar'

const sortFeedOptions: SortOption[] = [
  { label: 'Next Up', filter: 'JumpTime', order: 'sortAsc' },
  { label: 'Number of Copies', filter: 'Copies', order: 'sortDesc' },
  { label: 'Time Posted (Newest)', filter: 'ShareTime', order: 'sortDesc' },
  { label: 'Time Posted (Oldest)', filter: 'ShareTime', order: 'sortAsc' },
  { label: 'Highest Fixed Odds', filter: 'Odds', order: 'sortDesc' },
  { label: 'Lowest Fixed Odds', filter: 'Odds', order: 'sortAsc' },
]

export const SortFeed: React.FC<{
  sortOrder: SortOrder
  sortFilter: SortFilter
  handleSortOrderChange(order: SortOrder): void
  handleSortFilterChange(filter: SortFilter): void
}> = ({ sortFilter, sortOrder, handleSortFilterChange, handleSortOrderChange }) => {
  const [isSortTrayOpen, setIsSortTrayOpen] = React.useState(false)

  const selectedOption = getSelectedSortOptionLabel(sortOrder, sortFilter)

  const onSelectSortOption = (option: SortOption) => () => {
    const selected = sortFeedOptions.find(x => x.label === option.label)
    if (!selected) return

    handleSortOrderChange(option.order)
    handleSortFilterChange(option.filter)
    setIsSortTrayOpen(false)
    trackBetLoopSortFeed(option.label)
  }

  return (
    <>
      <SortButtonBar onClick={() => setIsSortTrayOpen(true)} label={selectedOption} />

      {createPortal(
        <Tray
          isOpen={isSortTrayOpen}
          headerTitle='Sort By'
          onDismiss={() => setIsSortTrayOpen(false)}
        >
          {sortFeedOptions.map(option => {
            const isSelected = option.label === selectedOption

            return (
              <SortOptionsListStyled onClick={onSelectSortOption(option)} key={option.label}>
                {option.label}
                {isSelected ? (
                  <Icon name='LineCheck' size='1.8rem' color={colors.lavender['700']} />
                ) : null}
              </SortOptionsListStyled>
            )
          })}
        </Tray>,
        document.body
      )}
    </>
  )
}

// =============
// Local Helpers
// =============

const getSelectedSortOptionLabel = (order: SortOrder, filter: SortFilter) => {
  const label = sortFeedOptions.find(option => option.order === order && option.filter === filter)
  return label?.label ?? sortFeedOptions[0].label
}

// ======
// Styles
// ======

const SortOptionsListStyled = styled.div({
  height: spacing.lgx1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  borderBottom: `0.05rem solid ${colors.neutral['200']}`,
  color: colors.black,
  fontSize: font.size.lg.fontSize,
  lineHeight: font.size.lg.lineHeight,
  fontWeight: font.weight.regular,
  cursor: 'pointer',
  WebkitTapHighlightColor: 'transparent',
  transition: 'color 0.15s ease, background 0.15s ease',

  '@media (hover: hover) and (pointer: fine)': {
    ':hover': { background: colors.neutral[100] },
  },

  ':active': {
    color: `color(from ${colors.black} srgb r g b / 0.5)`,
    background: colors.neutral[300],
  },
})
